// @flow
import * as React from 'react'
import { Document, Page } from 'react-pdf'
import { BSpinner } from '@prague-digi/ui-components'
import get from 'lodash/get'

import styles from './styles.scss'

type Props = {
  onPercentageChange?: Function
  onRenderedSuccess?: Function
  pdfBase64Data: string
  className?: string
  scale: number
  endCoefficient: number
  flowingPercentage?: boolean
}

type State = {
  /** number pages in PDF */
  numPages: number | null
  /** number loaded pages */
  renderedPages: number
  containerWidth: number
  preparing: boolean
  mounted: boolean
  percentageValue: number
}

const spinnerComp = <BSpinner isShown style={{ zIndex: 10 }} />

export class PdfView extends React.Component<Props, State> {
  state: State = {
    numPages: null,
    renderedPages: 0,
    containerWidth: 0,
    preparing: true,
    mounted: false,
    percentageValue: 0,
  }

  componentDidMount() {
    const setTimeoutCallback = () => {
      if (this.container) {
        this.setState({
          containerWidth: this.container.clientWidth - 15, // 15px vertical scrollbar width
          mounted: true,
        })
      }
    }

    setTimeout(setTimeoutCallback, 100)
  }

  componentDidUpdate(): void {
    if (this.end) {
      const boundaries = this.end.getBoundingClientRect()
      this.endInitTop = Math.max(boundaries.top, this.endInitTop)
    }
  }

  componentWillUnmount(): void {
    clearInterval(this.countPercentageIntervalRef)
  }

  onRenderPageSuccessTimeoutRef: any = null

  onDocumentLoadSuccess = ({ numPages }: State) => {
    this.setState({ numPages })
  }

  onRenderPageSuccess = () => {
    clearTimeout(this.onRenderPageSuccessTimeoutRef)
    this.onRenderPageSuccessTimeoutRef = setTimeout(() => {
      this.onRenderPageSuccess = () => null // destroy onRenderPageSuccess function

      const bounds = this.firstPageWrapper.getBoundingClientRect()
      this.minPageHeight = Math.max(this.minPageHeight, bounds.height)

      this.setState(
        {
          preparing: false,
        },
        () => {
          clearInterval(this.countPercentageIntervalRef)
          this.countPercentageIntervalRef = setInterval(this.countPercentage, 300)

          this.props.onRenderedSuccess && this.props.onRenderedSuccess()
        }
      )
    }, 300)
  }

  generatePages = (page: number | null): Array<string> =>
    page ? Array.from(Array(page), (d, i) => `page-${i}`) : []

  countPercentage = () => {
    if (this.state.mounted && this.container && this.end && this.endInitTop) {
      const windowHeight: number =
        window.innerHeight || get(document, 'documentElement.clientHeight')
      const rect = this.end.getBoundingClientRect()

      const rectTop = rect.top + (this.props.endCoefficient || 0) - windowHeight

      const endInitTop = this.endInitTop + (this.props.endCoefficient || 0) - windowHeight
      let percent = 100 - (100 / endInitTop) * rectTop

      percent = Math.floor(percent)
      percent = Math.max(0, percent) // percent < 0 ? 0 : percent
      percent = Math.min(100, percent) // > 100 ? 100 : percent

      const sameAsPrevious: boolean = this.state.percentageValue === percent

      if (!sameAsPrevious) {
        this.setState({ percentageValue: percent })
      }

      if (this.props.flowingPercentage && !sameAsPrevious) {
        this.props.onPercentageChange && this.props.onPercentageChange(percent)
      } else if (percent > this.maxPercentageValue) {
        this.maxPercentageValue = percent
        this.props.onPercentageChange && this.props.onPercentageChange(percent)
      }
    }
  }

  refContainer = (container: any) => {
    this.container = container
  }

  refEnd = (end: any) => {
    this.end = end
  }

  refPageWrapper = (pageWrapper: any, pageIndex: number) => {
    if (pageWrapper && pageIndex === 0) {
      this.firstPageWrapper = pageWrapper
    }
  }

  container?: HTMLDivElement
  end?: HTMLDivElement
  endInitTop: number = 0
  maxPercentageValue: number = 0
  countPercentageIntervalRef: any = null
  minPageHeight: number = 0
  firstPageWrapper: any

  render() {
    const pdfFile =
      this.props.pdfBase64Data && `data:application/pdf;base64,${this.props.pdfBase64Data}`
    const pagesArray = Array(...this.generatePages(this.state.numPages))

    const containerClassName = ['cont', this.props.className || '']

    // Compute index range to specify which pages can be rendered
    let fromIndex = 0
    if (pagesArray.length > 5 && this.state.percentageValue) {
      fromIndex = Math.max(
        0,
        Math.round((pagesArray.length / 100) * this.state.percentageValue) - 3
      )
    }
    const toIndex = fromIndex + 5

    return (
      <div ref={this.refContainer} className={containerClassName.join(' ')}>
        {this.state.preparing && spinnerComp}
        {pdfFile && this.state.mounted && this.state.containerWidth && (
          <Document
            file={pdfFile}
            onLoadSuccess={this.onDocumentLoadSuccess}
            renderMode="canvas"
            loading={null}
            noData={null}>
            {pagesArray.map((key, pageIndex) => (
              <div
                ref={self => this.refPageWrapper(self, pageIndex)}
                style={{ minHeight: `${this.minPageHeight}px` }}
                key={key}>
                {pageIndex >= fromIndex && pageIndex <= toIndex && (
                  <Page
                    key={key}
                    onRenderSuccess={this.onRenderPageSuccess}
                    pageIndex={pageIndex}
                    renderMode="canvas"
                    renderTextLayer={false}
                    scale={this.props.scale || 1}
                    width={this.state.containerWidth}
                    className={styles.pdfPage}
                  />
                )}
              </div>
            ))}
          </Document>
        )}
        <div ref={this.refEnd} />
      </div>
    )
  }
}

export default PdfView
